// PRE RMA — Valeurs de référence Paludisme (module Intrants) : lecture SEULE des modules Consultation (RCE) et CPN.
// Données locales d'abord (cache + file d'attente IndexedDB, saisies hors connexion incluses), rafraîchies automatiquement quand
// la synchronisation avance. Ce hook ne modifie rien : il n'alimente aucune formule de stock.
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  calculerReferencesPalu, calculerArtesunateParTranche, REFERENCES_PALU_VIDES, ARTESUNATE_PAR_TRANCHE_VIDE, fusionnerPatients, fusionnerSaisiesCpn, saisiesCpnComptees, calculerRmaCpn,
  patientsComptes,
} from '../../../shared/business-logic/index.ts';
import type { ReferencesPalu, ArtesunateParTranche, PatientServeur, ElementFile, CpnServeurLigne, CpnElementFile } from '../../../shared/business-logic/index.ts';
import { ligneDepuisConsultation } from '../../../shared/business-logic/calculLocal.ts';
import { chargerConsultationsCache, listerParPeriode as listerConsultationsFile } from '../offline/db.ts';
import { chargerCpnCache, listerParPeriode as listerCpnFile } from '../offline/dbCpn.ts';
import { ecouterEtatSynchro, rafraichirCacheConsultations } from '../offline/syncQueue.ts';
import { ecouterEtatSynchroCpn, rafraichirCacheCpn } from '../offline/syncQueueCpn.ts';
import type { Periode } from '../pages/Accueil.tsx';

export interface EtatReferencesPalu {
  references: ReferencesPalu;
  /** Détail palu grave (artésunate) par tranche d'âge. */
  artesunateParTranche: ArtesunateParTranche;
  /** false tant que les données locales ne sont pas lues (on n'affiche alors pas de « 0 » trompeur). */
  pret: boolean;
  nbConsultations: number;
  nbCpn: number;
}

const VIDE: EtatReferencesPalu = { references: REFERENCES_PALU_VIDES, artesunateParTranche: ARTESUNATE_PAR_TRANCHE_VIDE, pret: false, nbConsultations: 0, nbCpn: 0 };

async function calculer(periodeId: string): Promise<EtatReferencesPalu> {
  // --- Consultation (RCE) : serveur/cache + file locale, patients dont la suppression est en attente masqués
  const rowsRce = (await chargerConsultationsCache(periodeId)) as unknown as PatientServeur[];
  const fileRce = (await listerConsultationsFile(periodeId)) as unknown as ElementFile[];
  const toutSupprime = fileRce.some((i) => i.operation === 'DELETE_ALL' && i.syncStatus === 'PENDING');
  const { patients } = fusionnerPatients(toutSupprime ? [] : rowsRce, fileRce, 0);
  const comptes = patientsComptes(patients);
  const lignes = comptes.map((p) => ligneDepuisConsultation(p.data));

  // --- CPN : serveur/cache + file locale (suppressions en attente masquées par la fusion)
  const rowsCpn = (await chargerCpnCache(periodeId)) as unknown as CpnServeurLigne[];
  const fileCpn = (await listerCpnFile(periodeId)) as unknown as CpnElementFile[];
  const cpn = saisiesCpnComptees(fusionnerSaisiesCpn(rowsCpn, fileCpn));
  const { tpiEtMid } = calculerRmaCpn(cpn);

  return { references: calculerReferencesPalu(lignes, tpiEtMid), artesunateParTranche: calculerArtesunateParTranche(lignes), pret: true, nbConsultations: lignes.length, nbCpn: cpn.length };
}

export function useReferencesPalu(periode: Periode | null): EtatReferencesPalu {
  const [etat, setEtat] = useState<EtatReferencesPalu>(VIDE);
  const sequence = useRef(0); // ignore les calculs périmés (changement de période, synchro rapprochée)

  const recalculer = useCallback(async () => {
    if (!periode) return;
    const id = ++sequence.current;
    try {
      const r = await calculer(periode.id);
      if (id === sequence.current) setEtat(r);
    } catch {
      // IndexedDB indisponible : on n'invente rien, la colonne affichera « — ».
      if (id === sequence.current) setEtat({ ...VIDE, pret: false });
    }
  }, [periode?.id]);

  useEffect(() => {
    setEtat(VIDE);
    void recalculer(); // affichage immédiat depuis les données locales (fonctionne hors connexion)
    if (periode && navigator.onLine) {
      // Mise à jour discrète du cache depuis le serveur (saisies faites sur un autre appareil) ; échec réseau = on garde le local.
      void Promise.allSettled([rafraichirCacheConsultations(periode.id), rafraichirCacheCpn(periode.id)]).then(() => recalculer());
    }
    const arretRce = ecouterEtatSynchro(() => { void recalculer(); });
    const arretCpn = ecouterEtatSynchroCpn(() => { void recalculer(); });
    // Filet : retour au premier plan / retour du réseau (les saisies RCE et CPN ont pu changer depuis un autre écran).
    const surRetour = () => { void recalculer(); };
    window.addEventListener('online', surRetour);
    document.addEventListener('visibilitychange', surRetour);
    return () => { sequence.current++; arretRce(); arretCpn(); window.removeEventListener('online', surRetour); document.removeEventListener('visibilitychange', surRetour); };
  }, [periode?.id, recalculer]);

  return etat;
}
